import React, {useState} from 'react';
import {
    Grid,
    Box,
    Button,
    Paper,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import {
    School as SchoolIcon,
    Add as AddIcon,
} from '@mui/icons-material';
import CourseCard from '../../components/CourseCard.jsx';
import UpdatePopup from '../../components/UpdatePopup.jsx';

const CoursesSection = ({courses, onCourseCreated, apiClient}) => {
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState(null);
    const [courseData, setCourseData] = useState({
        title: '',
        description: '',
        academic_level: '',
    });

    // Akademische Level-Optionen
    const academicLevels = [
        { value: 'bachelor_1', label: 'Bachelor - 1. Semester' },
        { value: 'bachelor_2', label: 'Bachelor - 2. Semester' },
        { value: 'bachelor_3', label: 'Bachelor - 3. Semester' },
        { value: 'bachelor_4', label: 'Bachelor - 4. Semester' },
        { value: 'bachelor_5', label: 'Bachelor - 5. Semester' },
        { value: 'bachelor_6', label: 'Bachelor - 6. Semester' },
        { value: 'master_1', label: 'Master - 1. Semester' },
        { value: 'master_2', label: 'Master - 2. Semester' },
        { value: 'master_3', label: 'Master - 3. Semester' },
        { value: 'master_4', label: 'Master - 4. Semester' },
    ];

    // Edit course states
    const [showEditCourse, setShowEditCourse] = useState(false);
    const [selectedCourse, setSelectedCourse] = useState(null);

    const handleCreateCourse = async () => {
        if (!courseData.title.trim()) {
            setCreateError('Course title is required');
            return;
        }

        setCreating(true);
        setCreateError(null);

        try {

            const newCourse = await apiClient.post('/api/courses/', {
                title: courseData.title.trim(),
                description: courseData.description.trim(),
                academic_level: courseData.academic_level,
                teacher_id: "" // Backend will populate this with the authenticated teacher's ID
            });

            // Call the callback to refresh the courses list
            onCourseCreated(newCourse);

            setCourseData({title: '', description: '', academic_level: ''});
            setCreateDialogOpen(false);
        } catch (error) {
            console.error('Error creating course:', error);
            setCreateError(error.message || 'Failed to create course');
        } finally {
            setCreating(false);
        }
    };

    const handleCourseDeleted = (courseId) => {
        onCourseCreated();
    };

    // Handle course edit
    const handleEditCourse = (course) => {
        setSelectedCourse(course);
        setShowEditCourse(true);
    };

    const handleCloseEditCourse = () => {
        setShowEditCourse(false);
        setSelectedCourse(null);
    };

    const handleCourseUpdate = async (updatedCourse) => {
        // Refresh the courses list to show updated data
        await onCourseCreated();

        // Close the edit popup
        handleCloseEditCourse();
    };

    const handleDialogClose = () => {
        if (!creating) {
            setCreateDialogOpen(false);
            setCourseData({title: '', description: '', academic_level: ''});
            setCreateError(null);
        }
    };

    return (
        <>
            <Box sx={{mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <Typography variant="h5" sx={{display: 'flex', alignItems: 'center'}}>
                    <SchoolIcon sx={{mr: 1}}/>
                    My Courses
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon/>}
                    onClick={() => setCreateDialogOpen(true)}
                >
                    Create New Course
                </Button>
            </Box>

            {courses.length === 0 ? (
                <Paper sx={{p: 3, textAlign: 'center'}}>
                    <Typography variant="body1" gutterBottom>
                        You haven't created any courses yet.
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon/>}
                        onClick={() => setCreateDialogOpen(true)}
                        sx={{mt: 2}}
                    >
                        Create Your First Course
                    </Button>
                </Paper>
            ) : (
                <Grid container spacing={3}>
                    {courses.map((course) => (
                        <Grid size={{xs: 12, sm: 6}} key={course.id}>
                            <CourseCard
                                course={course}
                                onCourseDeleted={handleCourseDeleted}
                                onCourseEdit={handleEditCourse}
                                apiClient={apiClient}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Create Course Dialog */}
            <Dialog
                open={createDialogOpen}
                onClose={handleDialogClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    component: 'form',
                    onSubmit: (e) => {
                        e.preventDefault();
                        handleCreateCourse();
                    },
                }}
            >
                <DialogTitle>Create New Course</DialogTitle>
                <DialogContent>
                    {createError && (
                        <Alert severity="error" sx={{mb: 2}}>
                            {createError}
                        </Alert>
                    )}

                    <TextField
                        autoFocus
                        margin="dense"
                        label="Course Title"
                        fullWidth
                        variant="outlined"
                        value={courseData.title}
                        onChange={(e) => setCourseData({...courseData, title: e.target.value})}
                        disabled={creating}
                        required
                        sx={{mb: 2}}
                    />

                    <TextField
                        margin="dense"
                        label="Course Description"
                        fullWidth
                        variant="outlined"
                        multiline
                        rows={3}
                        value={courseData.description}
                        onChange={(e) => setCourseData({...courseData, description: e.target.value})}
                        disabled={creating}
                        placeholder="Optional description of the course"
                        sx={{mb: 2}} // NEU: Margin bottom hinzugefügt
                    />

                    {/* NEU: Academic Level Select */}
                    <FormControl fullWidth>
                        <InputLabel>Academic Level</InputLabel>
                        <Select
                            value={courseData.academic_level}
                            onChange={(e) => setCourseData({...courseData, academic_level: e.target.value})}
                            label="Academic Level"
                            disabled={creating}
                        >
                            {academicLevels.map((level) => (
                                <MenuItem key={level.value} value={level.value}>
                                    {level.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} disabled={creating}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={creating || !courseData.title.trim() || !courseData.description.trim()}
                        startIcon={creating ? <CircularProgress size={20}/> : <AddIcon/>}
                    >
                        {creating ? 'Creating...' : 'Create Course'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Course Popup */}
            {selectedCourse && (
                <UpdatePopup
                    open={showEditCourse}
                    onClose={handleCloseEditCourse}
                    onUpdate={handleCourseUpdate}
                    entityType="course"
                    entityData={selectedCourse}
                    apiClient={apiClient}
                    courseId={selectedCourse.id}
                />
            )}
        </>
    );
};

export default CoursesSection;